import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { ArrowRight, Beaker, Lightbulb, Users, TrendingUp, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { motion } from 'framer-motion';

const Counter = ({ end, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const startTime = Date.now();
        const step = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.floor(eased * end));
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);
  return <span ref={ref} className="counter-value text-white">{count.toLocaleString('fr-FR')}{suffix}</span>;
};

export const HomePage = () => {
  const [partnerIndex, setPartnerIndex] = useState(0);

  const partners = [
    'École Primaire Privée Vanves',
    'Groupe Scolaire La Maison de BAMBI',
    'École Primaire Privée Claire Fontaine',
    "Institut Secondaire d'Enseignement Technique (ISET)",
    'Groupe Scolaire Confessionnel Hinneh',
    'Institution Sainte Marie',
    'Lycée International Jean-Mermoz',
    'Groupe Scolaire Les Papillons',
  ];

  const visiblePartners = 4;
  const maxIndex = partners.length - visiblePartners;
  const nextPartner = () => setPartnerIndex(i => Math.min(i + 1, maxIndex));
  const prevPartner = () => setPartnerIndex(i => Math.max(i - 1, 0));

  const courses = ['Robotique', 'Programmation', 'Intelligence Artificielle', 'Modélisation 3D', 'Pilotage de drones', 'Anglais'];

  const benefits = [
    { icon: <Beaker className="w-8 h-8 text-white" />, title: 'Compétences STEM', desc: 'Développez des compétences en science, technologie, ingénierie et mathématiques à travers des projets pratiques et engageants.', color: 'bg-[#1B6FE3]' },
    { icon: <Lightbulb className="w-8 h-8 text-white" />, title: 'Créativité', desc: "Encouragez la pensée créative et l'innovation en construisant et programmant des robots uniques.", color: 'bg-[#F97316]' },
    { icon: <Users className="w-8 h-8 text-white" />, title: "Travail d'équipe", desc: "Apprenez à collaborer efficacement avec d'autres enfants sur des projets robotiques stimulants.", color: 'bg-emerald-500' },
    { icon: <TrendingUp className="w-8 h-8 text-white" />, title: 'Confiance en soi', desc: 'Renforcez la confiance en résolvant des défis techniques et en voyant vos créations prendre vie.', color: 'bg-purple-500' },
  ];

  return (
    <Layout>
      {/* HERO */}
      <section className="bg-[#1B6FE3] py-12 md:py-20 relative overflow-hidden" data-testid="hero-section">
        <div className="absolute top-10 right-10 w-64 h-64 bg-white/5 rounded-full"></div>
        <div className="absolute bottom-10 left-10 w-48 h-48 bg-[#F97316]/10 rounded-full"></div>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block bg-white/15 backdrop-blur-sm px-4 py-1.5 rounded-full text-sm font-bold text-white/90 mb-5">
                L'AVENIR COMMENCE ICI
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-5 leading-tight">
                Développez le potentiel de votre enfant grâce à la LUDOTECH
              </h1>
              <p className="text-base md:text-lg text-white/80 mb-8 leading-relaxed">
                Inspirez la prochaine génération d'innovateurs avec nos programmes de robotique et de programmation ludiques et créatifs.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/contact" data-testid="cta-essai-hero" className="btn-primary text-base px-8 py-4 justify-center">
                  Réserver un essai gratuit <ArrowRight size={18} />
                </Link>
                <Link to="/programmes" data-testid="cta-programmes" className="bg-white/15 backdrop-blur-sm border-2 border-white/30 text-white px-8 py-4 rounded-full font-bold text-base hover:bg-white/25 transition-all duration-200 text-center">
                  Découvrir nos programmes
                </Link>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="flex justify-center">
              <div className="relative">
                <div className="w-80 h-80 md:w-96 md:h-96 rounded-full bg-sky-300/30 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"></div>
                <img
                  src="https://horizons-cdn.hostinger.com/1b0ab6fa-0746-4a31-a6ec-811cdb30e480/5e493f0210407ebf91cc2cdf22364e31.png"
                  alt="Enfant portant un bras robotique"
                  className="relative z-10 w-72 md:w-96 mx-auto"
                  data-testid="hero-image"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* COURSES STRIP */}
      <section className="py-10 bg-white" data-testid="courses-strip">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-sm text-slate-500 font-bold uppercase tracking-wider mb-2">Ludotech Robotics — COURS DE</p>
              <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-[#1B6FE3] leading-snug">
                {courses.join(', ')}
              </h2>
              <div className="inline-block mt-4 border-2 border-[#1B6FE3] text-[#1B6FE3] font-bold text-sm px-5 py-2 rounded-full">
                DÈS 5 ANS
              </div>
            </div>
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-slate-600">Période scolaire : <strong className="text-[#F97316]">Mercredi et Samedi</strong></p>
                  <p className="text-sm text-slate-600">Vacances scolaire : <strong className="text-[#1B6FE3]">Lundi au Samedi</strong></p>
                </div>
                <div className="flex items-center text-sm text-slate-500">
                  <Clock size={14} className="mr-1.5" />
                  Horaires d'ouverture : de 9h à 18h
                </div>
                <div className="border-t border-slate-200 pt-3">
                  <p className="text-sm text-slate-600">Tarif : <strong className="text-[#F97316] text-lg">à partir de 3000F</strong></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NOS PROGRAMMES CTA */}
      <section className="py-16 md:py-20 section-light-blue" data-testid="programmes-cta">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center max-w-3xl">
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-slate-900 mb-4">Nos Programmes</h2>
          <p className="text-slate-600 text-base leading-relaxed mb-8">
            Un apprentissage progressif conçu pour accompagner votre enfant à chaque étape de son développement, de la découverte ludique à la maîtrise technique.
          </p>
          <Link to="/programmes" className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-8 py-4 rounded-full font-bold transition-all duration-200">
            Découvrir tous nos programmes <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* POURQUOI CHOISIR */}
      <section className="py-16 md:py-20 bg-white" data-testid="why-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-slate-900 text-center mb-10">
            Pourquoi choisir la LudoTech ?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-4xl mx-auto">
            {benefits.map((b, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.1 }} viewport={{ once: true }}
                className="tk-card flex items-start gap-4 group" data-testid={`benefit-${i}`}>
                <div className={`${b.color} w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110 group-hover:rotate-3`}>
                  {b.icon}
                </div>
                <div>
                  <h3 className="font-heading text-lg font-extrabold text-slate-900 mb-1">{b.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{b.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ILS NOUS FONT CONFIANCE */}
      <section className="py-16 md:py-20 section-cream" data-testid="partners-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-[#F97316] text-center mb-10 italic">
            Ils nous font confiance
          </h2>
          <div className="relative max-w-5xl mx-auto">
            <button onClick={prevPartner} disabled={partnerIndex === 0}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-slate-50 disabled:opacity-30 transition-all" data-testid="partner-prev">
              <ChevronLeft size={20} className="text-slate-600" />
            </button>
            <div className="overflow-hidden">
              <div className="flex transition-transform duration-500" style={{ transform: `translateX(-${partnerIndex * (100 / visiblePartners)}%)` }}>
                {partners.map((name, i) => (
                  <div key={i} className="flex-shrink-0 px-3" style={{ width: `${100 / visiblePartners}%` }}>
                    <div className="bg-white rounded-2xl p-6 text-center border border-slate-100 hover:shadow-md transition-shadow h-full flex flex-col items-center justify-center min-h-[140px]">
                      <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mb-3">
                        <svg className="w-7 h-7 text-[#1B6FE3]/40" fill="currentColor" viewBox="0 0 24 24"><path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z"/></svg>
                      </div>
                      <p className="font-heading text-sm font-bold text-slate-800">{name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <button onClick={nextPartner} disabled={partnerIndex >= maxIndex}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-slate-50 disabled:opacity-30 transition-all" data-testid="partner-next">
              <ChevronRight size={20} className="text-slate-600" />
            </button>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-16 md:py-20 bg-[#1B6FE3] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute top-0 left-0 w-72 h-72 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-heading text-3xl md:text-5xl font-black text-white mb-5">
            Offrez à votre enfant une expérience unique
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Inscrivez votre enfant dès maintenant et donnez-lui les clés du futur.
          </p>
          <Link to="/contact" className="btn-primary text-base px-10 py-4 inline-flex items-center gap-2">
            Inscrire mon enfant <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
